import { ExportButton } from '@/common/buttons/ExportButton';
import { TitleButton } from '@/common/buttons/TitleButton';
import { ActionDialog } from '@/common/dialog/ActionDialog';
import { useDialog } from '@/common/dialog/DialogContext';
import { useTab } from '@/common/entityFramework/entityDetail/TabContext';
import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AbvOpenPeriodToggleButton } from './AbvOpenPeriodToggleButton';

export const AbvManAccrAdjList = () => {

  const rsrc = useRsrc();

  const module = useModuleFromRoute();
  const entityListRef = useRef();

  const { openDialog } = useDialog();

  // NavState
  const { navState, assign } = useNavState();
  const { tabId } = useTab();
  const manAccrAdjStateId = tabId ? tabId + 'ManAccrAdjState' : 'ManAccrAdjState';
  const manAccrAdjState = navState[manAccrAdjStateId] ?? {};

  const [paidPeriodFlag, setPaidPeriodFlag] = useState(manAccrAdjState.paidPeriodFlag ?? true);

  const entity = 'MAN_ACCR_ADJ';

  // ----- NavState -----
  const saveNavState = () => {
    const newManAccrAdjState = {
      paidPeriodFlag
    }

    assign(manAccrAdjStateId, newManAccrAdjState);
  }

  useEffect(() => {
    saveNavState();
  }, [paidPeriodFlag]);

  // ----- Data -----
  const clientSideRowData = useCallback((setDataSource) => {

    const dataPlugin = navState.pluginCode ?? 'MAN_ACCR_ADJ_OPEN_PERIOD_DATA_PLUGIN';
    const reqParam = {
      ...navState.reqParam,
      entity,
      paidPeriodFlag
    };

    rsrc.data(module, dataPlugin, reqParam, entity).then(res => {
      setDataSource(res);
      totalsRow();
    });

  }, [paidPeriodFlag]);

  const totalsRow = () => {

    const gridRef = entityListRef.current.gridRef;
    if (!paidPeriodFlag) {
      gridRef.current.setTotalsRow(null);
      return;
    }

    const totals = {
      code: 'TOTAL',
      amt: 0
    };

    const api = gridRef.current.gridApiRef.current;
    api.forEachNodeAfterFilterAndSort((node) => {
      totals.amt = totals.amt + node.data.amt;
    });

    gridRef.current.setTotalsRow(totals);

  };

  const onFilterChanged = () => {
    totalsRow();
  };

  // ----- Actions -----
  const onBulkAdj = () => {

    const api = entityListRef.current.gridRef.current.gridApiRef.current;
    const fieldDefs = entityListRef.current.fieldDefs;

    const expr = ExprUtils.filterModelToExpr(api.getFilterModel(), fieldDefs);

    actionDialog(
      { addlReqData: { expr } },
      'Bulk Adjustment',
      'BULK_ADJ_MAN_ACCR_ADJ_ACTION_PLUGIN'
    );
  };

  const augButtons = useCallback((buttons) => {

    // TODO: Remove from Entity List in general ??
    const exportOnly = buttons.filter((btn) => btn.type === ExportButton);
    buttons.splice(0, buttons.length, ...exportOnly);

    // Do not render any other button if routed from another Entity
    if (!navState.pluginCode) {
      if (paidPeriodFlag) {
        buttons.push(
          <TitleButton toolTip={'Bulk Adjustment'} icon={fas.faCopy} dataTestId="bulk-adj" onClick={onBulkAdj}></TitleButton>
        );
      }

      buttons.push(<AbvOpenPeriodToggleButton paidPeriodFlag={paidPeriodFlag} setPaidPeriodFlag={setPaidPeriodFlag} />);
    }

  }, [paidPeriodFlag]);

  const getContextMenuItems = (params) => {
    let contextMenus = entityListRef.current.getContextMenuItems(params);

    // Do not allow to run an Action on when routed from another Entity
    if (!navState.pluginCode) {

      // Splitting
      let splittingData = {
        entityId: params.node.data.id
      }

      contextMenus.push({
        name: 'Splitting',
        action: () => actionDialog(splittingData, 'Splitting', 'SPLIT_MAN_ACCR_ADJ_ACTION_PLUGIN')
      });
    }

    return contextMenus;
  }

  const actionDialog = (data, title, pluginCode) => {
    openDialog(ActionDialog, {
      data:
      {
        ...data,
        title,
        pluginCode,
        entity
      }
    });
  }

  return (
    <EntityList
      ref={entityListRef}
      entity={entity}
      clientSideRowData={clientSideRowData}
      augButtons={augButtons}
      gridOptions={{
        getContextMenuItems
      }}
      events={{
        onFilterChanged
      }}
    />
  )
}